/**
 * Booking Service - Backend API Integration
 *
 * Handles all booking-related API calls to the backend
 */

import { post, get, put } from "../utils/api";

/**
 * Create a new booking appointment
 *
 * @param {Object} bookingData - Booking information
 * @param {string} bookingData.businessId - Business ID
 * @param {string} bookingData.serviceId - Service ID
 * @param {string} bookingData.serviceName - Service name
 * @param {Date} bookingData.date - Booking date
 * @param {string} bookingData.time - Booking time
 * @param {Object} bookingData.clientInfo - Client information
 * @param {string} bookingData.clientInfo.fullName - Full name
 * @param {string} bookingData.clientInfo.email - Email
 * @param {string} bookingData.clientInfo.phone - Phone number
 * @param {string} bookingData.clientInfo.message - Optional message
 * @param {Object} bookingData.paymentInfo - Payment information (if paid)
 * @param {string} bookingData.paymentInfo.transactionId - Payment transaction ID
 * @param {string} bookingData.paymentInfo.confirmationCode - Payment confirmation code
 * @param {string} bookingData.paymentInfo.paymentMethod - Payment method used
 * @returns {Promise<Object>} Created booking
 */
export const createBooking = async (bookingData) => {
  try {
    const response = await post("/bookings", {
      businessId: bookingData.businessId,
      serviceId: bookingData.serviceId,
      serviceName: bookingData.serviceName,
      date: bookingData.date.toISOString(),
      time: bookingData.time,
      clientInfo: bookingData.clientInfo,
      paymentInfo: bookingData.paymentInfo,
      status: bookingData.paymentInfo ? "confirmed" : "pending",
    });

    return {
      success: true,
      booking: response.booking,
      bookingId: response.booking.id,
      confirmationCode: response.booking.confirmationCode,
    };
  } catch (error) {
    console.error("Create booking error:", error);
    return {
      success: false,
      error: error.message || "Failed to create booking",
    };
  }
};

/**
 * Get booking by ID
 *
 * @param {string} bookingId - Booking ID
 * @returns {Promise<Object>} Booking details
 */
export const getBooking = async (bookingId) => {
  try {
    const response = await get(`/bookings/${bookingId}`);
    return {
      success: true,
      booking: response.booking,
    };
  } catch (error) {
    console.error("Get booking error:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch booking",
    };
  }
};

/**
 * Update booking
 *
 * @param {string} bookingId - Booking ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Updated booking
 */
export const updateBooking = async (bookingId, updates) => {
  try {
    const response = await put(`/bookings/${bookingId}`, updates);
    return {
      success: true,
      booking: response.booking,
    };
  } catch (error) {
    console.error("Update booking error:", error);
    return {
      success: false,
      error: error.message || "Failed to update booking",
    };
  }
};

/**
 * Cancel booking
 *
 * @param {string} bookingId - Booking ID
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelBooking = async (bookingId, reason) => {
  try {
    const body = { status: "cancelled" };
    if (reason) body.cancellation_reason = reason;
    await put(`/bookings/${bookingId}`, body);
    return { success: true, message: "Booking cancelled successfully" };
  } catch (error) {
    console.error("Cancel booking error:", error);
    return { success: false, error: error.message || "Failed to cancel booking" };
  }
};

/**
 * Submit booking request by business slug (Step 37)
 * POST /api/public/businesses/:slug/booking-requests
 *
 * @param {string} slug - Business slug or id (from URL)
 * @param {Object} data - service_id, customer_name, customer_phone, preferred_date, preferred_time, note
 */
export const submitBookingRequestBySlug = async (slug, data) => {
  try {
    const res = await post(`/public/businesses/${encodeURIComponent(slug)}/booking-requests`, data);
    return { success: true, id: res.id, message: res.message };
  } catch (err) {
    return { success: false, error: err.message || "Failed to submit request" };
  }
};

/**
 * Submit booking request (legacy)
 * POST /api/public/booking-requests
 *
 * @param {Object} data - business_id, service_id, preferred_date (YYYY-MM-DD), preferred_time (HH:mm), client_name, client_email, client_phone, notes
 */
export const submitBookingRequest = async (data) => {
  try {
    const res = await post("/public/booking-requests", data);
    return { success: true, id: res.id, message: res.message };
  } catch (err) {
    return { success: false, error: err.message || "Failed to submit request" };
  }
};

/**
 * List bookings for the current client (requires auth, role=client)
 * GET /api/bookings — server filters by client_id from JWT
 */
export const listMyBookings = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.page) params.set("page", filters.page);
    if (filters.limit) params.set("limit", filters.limit);
    const query = params.toString();
    const res = await get(`/bookings${query ? `?${query}` : ""}`);
    return { success: true, data: res.data || [], meta: res.meta || {} };
  } catch (err) {
    return { success: false, error: err.message || "Failed to fetch bookings", data: [] };
  }
};

/**
 * List bookings for a business (requires auth)
 * GET /api/bookings?business_id=X
 */
export const listBusinessBookings = async (businessId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.set("business_id", businessId);
    if (filters.status) params.set("status", filters.status);
    if (filters.page) params.set("page", filters.page);
    if (filters.limit) params.set("limit", filters.limit);
    const res = await get(`/bookings?${params}`);
    return { success: true, data: res.data || [], meta: res.meta || {} };
  } catch (err) {
    return { success: false, error: err.message || "Failed to fetch bookings", data: [] };
  }
};

/**
 * Update booking status (approve/decline)
 * PUT /api/bookings/:id { status }
 */
export const updateBookingStatus = async (bookingId, status) => {
  try {
    await put(`/bookings/${bookingId}`, { status });
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message || "Failed to update" };
  }
};

/** @deprecated Use listBusinessBookings */
export const getBusinessBookings = async (businessId, filters = {}) => {
  const r = await listBusinessBookings(businessId, filters);
  return r.success ? { success: true, bookings: r.data } : r;
};

export default {
  createBooking,
  getBooking,
  updateBooking,
  cancelBooking,
  getBusinessBookings,
};
